import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Building2, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/business-signup")({
  head: () => ({
    meta: [
      { title: "Business Sign Up — Bacha Way Travel" },
      {
        name: "description",
        content:
          "Join the Bacha Way Travel B2B network. Hotels, riads, DMCs and tour operators get preferential rates and a dedicated dashboard.",
      },
    ],
  }),
  component: BusinessSignupPage,
});

const COMPANY_TYPES = ["Hotel", "Riad", "DMC", "Tour Operator", "Other"] as const;

const schema = z.object({
  companyName: z.string().trim().min(1, "Required").max(120),
  personName: z.string().trim().min(1, "Required").max(120),
  personRole: z.string().trim().min(1, "Required").max(120),
  companyType: z.enum(COMPANY_TYPES),
  address: z.string().trim().min(1, "Required").max(300),
  email: z.string().trim().email("Invalid email").max(200),
  phone: z.string().trim().min(5, "Required").max(40),
  website: z
    .string()
    .trim()
    .max(200)
    .optional()
    .or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

function BusinessSignupPage() {
  const [form, setForm] = useState({
    companyName: "",
    personName: "",
    personRole: "",
    companyType: "Hotel" as (typeof COMPANY_TYPES)[number],
    address: "",
    email: "",
    phone: "",
    website: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check the form");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/business-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) throw new Error("Request failed");
      navigate({ to: "/business-signup/thanks" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-hero-navy text-white">
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/80">
            <Building2 className="h-3.5 w-3.5" /> B2B Partner Program
          </div>
          <h1 className="mt-6 text-4xl md:text-5xl font-bold leading-[1.05]">
            Join the <span className="text-gradient-sky">Bacha Way</span> network
          </h1>
          <p className="mt-4 mx-auto max-w-2xl text-white/70">
            Hotels, riads, DMCs and tour operators — centralise your guest
            transfers with preferential rates and a dedicated dashboard.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-background">
        <div className="mx-auto max-w-2xl px-6">
          <form
            onSubmit={onSubmit}
            className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Company name *">
                <Input
                  value={form.companyName}
                  onChange={(e) => update("companyName", e.target.value)}
                  required
                  maxLength={120}
                />
              </Field>
              <Field label="Type *">
                <Select
                  value={form.companyType}
                  onValueChange={(v) =>
                    update("companyType", v as (typeof COMPANY_TYPES)[number])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Contact person *">
                <Input
                  value={form.personName}
                  onChange={(e) => update("personName", e.target.value)}
                  required
                  maxLength={120}
                />
              </Field>
              <Field label="Function / Role *">
                <Input
                  value={form.personRole}
                  onChange={(e) => update("personRole", e.target.value)}
                  placeholder="e.g. General Manager"
                  required
                  maxLength={120}
                />
              </Field>
              <Field label="Email *">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                  maxLength={200}
                />
              </Field>
              <Field label="Phone number *">
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  required
                  maxLength={40}
                />
              </Field>
            </div>

            <Field label="Address *">
              <Input
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                required
                maxLength={300}
              />
            </Field>

            <Field label="Website (optional)">
              <Input
                type="url"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                placeholder="https://"
                maxLength={200}
              />
            </Field>

            <Field label="Message (optional)">
              <Textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Tell us about your volumes, routes, or any specific needs."
              />
            </Field>

            <Button type="submit" disabled={submitting} className="w-full" size="lg">
              <Send className="h-4 w-4" />
              {submitting ? "Sending…" : "Send request"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              We&apos;ll email you a confirmation and reach out as soon as possible.
            </p>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}
